import {
  V2_ALLOWED_KNOWLEDGE_ITEM_TYPES,
  V2_ALLOWED_LANGUAGES,
  V2_DEFAULT_ADMIN_APP_SETTINGS,
  V2_FOOD_ICON_KEYS,
  V2_KNOWLEDGE_ITEM_TYPES,
} from "./v2Schema";
import { getFoodIconKey, toFoodNumber } from "./v2FoodUtils";

export function validateAdminMainFood(input = {}) {
  const errors = {};

  requireText(input.name_en, "name_en", "Food name English is required.", errors);
  requireText(input.category, "category", "Category is required.", errors);
  requireText(input.serving_unit, "serving_unit", "Serving unit is required.", errors);
  requireText(input.source, "source", "Source is required.", errors);
  validatePositive(input.serving_quantity, "serving_quantity", "Serving quantity must be greater than 0.", errors);
  validateZeroOrPositive(input.potassium_mg, "potassium_mg", "Potassium must be 0 or greater.", errors);
  validateZeroOrPositive(input.phosphorus_mg, "phosphorus_mg", "Phosphorus must be 0 or greater.", errors);
  validateZeroOrPositive(input.sodium_mg, "sodium_mg", "Sodium must be 0 or greater.", errors);
  validateZeroOrPositive(input.protein_g, "protein_g", "Protein must be 0 or greater.", errors);

  return validationResult(errors);
}

export function prepareAdminMainFoodPayload(input = {}) {
  const validation = validateAdminMainFood(input);
  if (!validation.valid) return { validation, payload: null };

  return {
    validation,
    payload: {
      name_en: String(input.name_en).trim(),
      name_ar: String(input.name_ar || "").trim(),
      category: String(input.category).trim(),
      serving_quantity: toFoodNumber(input.serving_quantity),
      serving_unit: String(input.serving_unit).trim(),
      potassium_mg: toFoodNumber(input.potassium_mg),
      phosphorus_mg: toFoodNumber(input.phosphorus_mg),
      sodium_mg: toFoodNumber(input.sodium_mg),
      protein_g: toFoodNumber(input.protein_g),
      source: String(input.source).trim(),
      verified: input.verified !== false,
      active: input.active !== false,
      icon_key: getFoodIconKey(input.icon_key || V2_FOOD_ICON_KEYS.default),
    },
  };
}

export function validateApprovedTip(input = {}) {
  const errors = {};

  requireText(input.title_en, "title_en", "English title is required.", errors);
  requireText(input.body_en, "body_en", "English body is required.", errors);
  requireText(input.category, "category", "Category is required.", errors);

  return validationResult(errors);
}

export function prepareApprovedTipPayload(input = {}) {
  const validation = validateApprovedTip(input);
  if (!validation.valid) return { validation, payload: null };

  return {
    validation,
    payload: {
      title_en: String(input.title_en).trim(),
      title_ar: String(input.title_ar || "").trim(),
      body_en: String(input.body_en).trim(),
      body_ar: String(input.body_ar || "").trim(),
      category: String(input.category).trim(),
      active: input.active !== false,
      reviewed: Boolean(input.reviewed),
    },
  };
}

export function validateQuote(input = {}) {
  const errors = {};

  requireText(input.quote_en, "quote_en", "English quote is required.", errors);

  if (input.display_date && !isValidDate(input.display_date)) {
    errors.display_date = "Display date must be a valid date.";
  }

  return validationResult(errors);
}

export function prepareQuotePayload(input = {}) {
  const validation = validateQuote(input);
  if (!validation.valid) return { validation, payload: null };

  return {
    validation,
    payload: {
      quote_en: String(input.quote_en).trim(),
      quote_ar: String(input.quote_ar || "").trim(),
      active: input.active !== false,
      display_date: String(input.display_date || "").trim(),
    },
  };
}

export function validateKnowledgeItem(input = {}) {
  const errors = {};

  requireText(input.title, "title", "Title is required.", errors);
  requireText(input.category, "category", "Category is required.", errors);

  if (!V2_ALLOWED_KNOWLEDGE_ITEM_TYPES.includes(input.type)) {
    errors.type = "Choose a valid knowledge type.";
  }

  if (!V2_ALLOWED_LANGUAGES.includes(input.language)) {
    errors.language = "Choose a valid language.";
  }

  if ([V2_KNOWLEDGE_ITEM_TYPES.pdf, V2_KNOWLEDGE_ITEM_TYPES.docx].includes(input.type)) {
    requireText(input.file_name, "file_name", "File name metadata is required.", errors);
  }

  if ([V2_KNOWLEDGE_ITEM_TYPES.manualNote, V2_KNOWLEDGE_ITEM_TYPES.txt, V2_KNOWLEDGE_ITEM_TYPES.markdown].includes(input.type)) {
    requireText(input.content, "content", "Content is required.", errors);
  }

  return validationResult(errors);
}

export function prepareKnowledgeItemPayload(input = {}) {
  const validation = validateKnowledgeItem(input);
  if (!validation.valid) return { validation, payload: null };

  return {
    validation,
    payload: {
      title: String(input.title).trim(),
      type: input.type,
      language: input.language,
      category: String(input.category).trim(),
      active: input.active !== false,
      reviewed: Boolean(input.reviewed),
      source_notes: String(input.source_notes || "").trim(),
      content: String(input.content || "").trim(),
      file_name: String(input.file_name || "").trim(),
    },
  };
}

export function mergeAdminAppSettings(settings = {}) {
  return {
    ...V2_DEFAULT_ADMIN_APP_SETTINGS,
    ...settings,
    active_languages: Array.isArray(settings.active_languages)
      ? settings.active_languages.filter((language) => V2_ALLOWED_LANGUAGES.includes(language))
      : V2_DEFAULT_ADMIN_APP_SETTINGS.active_languages,
    daily_system_reminder_time:
      isValidTime(settings.daily_system_reminder_time)
        ? settings.daily_system_reminder_time
        : V2_DEFAULT_ADMIN_APP_SETTINGS.daily_system_reminder_time,
    guidance_text: String(settings.guidance_text || V2_DEFAULT_ADMIN_APP_SETTINGS.guidance_text).trim(),
    whatsapp_provider_configured: Boolean(settings.whatsapp_provider_configured),
    default_whatsapp_message: String(
      settings.default_whatsapp_message || V2_DEFAULT_ADMIN_APP_SETTINGS.default_whatsapp_message
    ).trim(),
    whatsapp_generic_message_default:
      settings.whatsapp_generic_message_default ?? V2_DEFAULT_ADMIN_APP_SETTINGS.whatsapp_generic_message_default,
  };
}

export function createAuditLogEntry({ action, targetType, targetId = "", details = "" } = {}) {
  return {
    action: String(action || "").trim(),
    target_type: String(targetType || "").trim(),
    target_id: String(targetId || "").trim(),
    details: String(details || "").trim(),
  };
}

function validationResult(errors) {
  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

function requireText(value, key, message, errors) {
  if (!String(value || "").trim()) {
    errors[key] = message;
  }
}

function validatePositive(value, key, message, errors) {
  const parsed = Number(value);
  if (!String(value ?? "").trim() || !Number.isFinite(parsed) || parsed <= 0) {
    errors[key] = message;
  }
}

function validateZeroOrPositive(value, key, message, errors) {
  const parsed = Number(value);
  if (!String(value ?? "").trim() || !Number.isFinite(parsed) || parsed < 0) {
    errors[key] = message;
  }
}

function isValidDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return false;
  return !Number.isNaN(new Date(`${value}T00:00:00`).getTime());
}

function isValidTime(value) {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(String(value || ""));
}
